import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import { PageLayout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  Sparkles, ArrowLeft, ArrowRight, Heart, Brain, Star, Zap, Loader2,
  CheckCircle2, Share2, Eye, EyeOff, RefreshCcw, Wand2, GripVertical, Download
} from "lucide-react";

const STEPS = ["working-style", "themes", "values", "genius", "results"];

const StepHeader = ({ idx, label, sub }) => (
  <div>
    <p className="text-[#FFD507] text-xs uppercase tracking-[0.25em] font-bold mb-2">Step {idx + 1} of 4</p>
    <h1 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900">{label}</h1>
    {sub && <p className="text-slate-600 mt-2 max-w-2xl">{sub}</p>}
  </div>
);

const SelectableCard = ({ active, onClick, title, statement, badge, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative text-left border rounded-xl p-4 transition w-full ${
      active
        ? "border-[#FFD507] bg-[#FFD507]/10 ring-2 ring-[#FFD507]/30"
        : "border-slate-200 hover:border-[#1E3A8A]/40 hover:bg-slate-50"
    }`}
    data-testid={testid}
  >
    {badge && (
      <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#FFD507] text-[#0F1B3D] text-xs font-bold flex items-center justify-center">
        {badge}
      </span>
    )}
    <h3 className="font-heading font-bold text-slate-900">{title}</h3>
    <p className="text-sm text-slate-600 mt-1">{statement}</p>
  </button>
);

const StrengthsProfile = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [content, setContent] = useState(null);
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [answers, setAnswers] = useState({
    working_style: {},
    top_themes: [],
    top_values: [],
    working_genius_ranking: [],
  });
  const [genNarrative, setGenNarrative] = useState(false);

  // Load assessment content + existing profile
  useEffect(() => {
    (async () => {
      try {
        const [cRes, pRes] = await Promise.all([
          fetch(`${API}/strengths/content`).then((r) => r.json()),
          fetch(`${API}/strengths/profile`, { credentials: "include" }).then((r) => r.json()),
        ]);
        setContent(cRes);
        if (pRes?.completed) {
          setExisting(pRes.profile);
          setAnswers({
            working_style: pRes.profile.working_style || {},
            top_themes: pRes.profile.top_themes || [],
            top_values: pRes.profile.top_values || [],
            working_genius_ranking: pRes.profile.working_genius_ranking || [],
          });
          setStep(4);
        } else {
          // Seed working_genius_ranking with default order so drag-rank UI has items
          setAnswers((a) => ({
            ...a,
            working_genius_ranking: cRes.working_genius_areas.map((g) => g.id),
          }));
        }
      } catch (e) {
        toast.error("Could not load assessment");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const progress = (step / 4) * 100;

  const toggleTheme = (id) =>
    setAnswers((a) => {
      const picked = a.top_themes.includes(id);
      if (picked) return { ...a, top_themes: a.top_themes.filter((x) => x !== id) };
      if (a.top_themes.length >= 5) {
        toast.info("Top 5 themes only — remove one first");
        return a;
      }
      return { ...a, top_themes: [...a.top_themes, id] };
    });

  const toggleValue = (id) =>
    setAnswers((a) => {
      const picked = a.top_values.includes(id);
      if (picked) return { ...a, top_values: a.top_values.filter((x) => x !== id) };
      if (a.top_values.length >= 5) {
        toast.info("Top 5 values only — remove one first");
        return a;
      }
      return { ...a, top_values: [...a.top_values, id] };
    });

  const moveGenius = (idx, dir) =>
    setAnswers((a) => {
      const list = [...a.working_genius_ranking];
      const j = idx + dir;
      if (j < 0 || j >= list.length) return a;
      [list[idx], list[j]] = [list[j], list[idx]];
      return { ...a, working_genius_ranking: list };
    });

  // ── Drag-and-drop reorder (HTML5 native) ──────────────────────────
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const reorderGenius = (from, to) =>
    setAnswers((a) => {
      if (from === to || from == null || to == null) return a;
      const list = [...a.working_genius_ranking];
      const [moved] = list.splice(from, 1);
      list.splice(to, 0, moved);
      return { ...a, working_genius_ranking: list };
    });

  const canAdvance = useMemo(() => {
    if (step === 0) return Object.keys(answers.working_style).length === content?.working_style_questions?.length;
    if (step === 1) return answers.top_themes.length === 5;
    if (step === 2) return answers.top_values.length === 5;
    if (step === 3) return answers.working_genius_ranking.length === 6;
    return true;
  }, [step, answers, content]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/strengths/profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(answers),
      });
      const text = await res.text();
      let data; try { data = JSON.parse(text); } catch { data = {}; }
      if (!res.ok) throw new Error(data?.detail || "Save failed");
      setExisting(data.profile);
      setStep(4);
      toast.success("Strengths profile saved");
      // Kick off AI narrative in background
      generateNarrative(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const generateNarrative = async (silent = false) => {
    setGenNarrative(true);
    try {
      const res = await fetch(`${API}/strengths/ai-narrative`, { method: "POST", credentials: "include" });
      const text = await res.text();
      let data; try { data = JSON.parse(text); } catch { data = {}; }
      if (!res.ok) throw new Error(data?.detail || "AI failed");
      setExisting((p) => ({ ...(p || {}), ai_narrative: data.narrative }));
      if (!silent) toast.success("Narrative generated");
    } catch (err) {
      if (!silent) toast.error(err.message);
    } finally {
      setGenNarrative(false);
    }
  };

  const toggleShare = async (field, value) => {
    try {
      const res = await fetch(`${API}/strengths/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ [field]: value }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Update failed");
      setExisting(data.profile);
      toast.success("Sharing updated");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${API}/strengths/my-profile-pdf`, { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Couldn't generate PDF");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "strengths_profile.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded");
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading || !content) {
    return (
      <PageLayout>
        <div className="text-center py-20"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto" data-testid="strengths-page">
        {/* Hero */}
        <div className="bg-gradient-to-br from-[#0F1B3D] via-[#1E3A8A] to-[#0A1A4A] text-white rounded-2xl p-7 md:p-10 mb-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-72 h-72 bg-[#FFD507]/10 rounded-full blur-3xl pointer-events-none" />
          <p className="text-[#FFD507] text-xs uppercase tracking-[0.25em] font-bold mb-2 flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> My Strengths Profile
          </p>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold leading-tight">
            Discover how you work best.
          </h1>
          <p className="text-slate-300 mt-3 max-w-2xl">
            A short, plain-language assessment that combines your <span className="text-white font-medium">Working Style</span>,{" "}
            <span className="text-white font-medium">top themes</span>,{" "}
            <span className="text-white font-medium">core values</span>, and{" "}
            <span className="text-white font-medium">Working Genius</span>. About 5 minutes.
          </p>
          {step < 4 && (
            <div className="mt-6">
              <Progress value={progress} className="bg-white/10 h-2" />
              <p className="text-xs text-white/60 mt-2">{Math.round(progress)}% complete</p>
            </div>
          )}
        </div>

        {/* Step 0: Working Style */}
        {step === 0 && (
          <Card className="border border-slate-200" data-testid="step-working-style">
            <CardHeader>
              <CardTitle>
                <StepHeader idx={0} label="Your Working Style" sub="Five quick questions about how you work best. Pick what fits — there are no wrong answers." />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {content.working_style_questions.map((q) => (
                <div key={q.id}>
                  <p className="font-semibold text-slate-900 mb-3">{q.label}</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {q.options.map((opt) => {
                      const active = answers.working_style[q.id] === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setAnswers((a) => ({ ...a, working_style: { ...a.working_style, [q.id]: opt.value } }))}
                          className={`text-left border rounded-lg p-3 text-sm transition ${
                            active ? "border-[#FFD507] bg-[#FFD507]/10" : "border-slate-200 hover:border-[#1E3A8A]/40 hover:bg-slate-50"
                          }`}
                          data-testid={`ws-${q.id}-${opt.value}`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Step 1: Themes */}
        {step === 1 && (
          <Card className="border border-slate-200" data-testid="step-themes">
            <CardHeader>
              <CardTitle>
                <StepHeader
                  idx={1}
                  label="Pick your top 5 strengths"
                  sub="From the 12 themes below, choose the 5 that feel most true to you. (Clifton-inspired, plain language.)"
                />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 mb-3">
                Selected: <span className="font-bold text-slate-900">{answers.top_themes.length}</span> / 5
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {content.themes.map((t) => {
                  const rank = answers.top_themes.indexOf(t.id);
                  return (
                    <SelectableCard
                      key={t.id}
                      active={rank >= 0}
                      onClick={() => toggleTheme(t.id)}
                      title={t.name}
                      statement={t.statement}
                      badge={rank >= 0 ? rank + 1 : null}
                      testid={`theme-${t.id}`}
                    />
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Values */}
        {step === 2 && (
          <Card className="border border-slate-200" data-testid="step-values">
            <CardHeader>
              <CardTitle>
                <StepHeader idx={2} label="Pick your top 5 values" sub="What matters most to you in your work and your life?" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 mb-3">
                Selected: <span className="font-bold text-slate-900">{answers.top_values.length}</span> / 5
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {content.values.map((v) => {
                  const rank = answers.top_values.indexOf(v.id);
                  return (
                    <SelectableCard
                      key={v.id}
                      active={rank >= 0}
                      onClick={() => toggleValue(v.id)}
                      title={v.name}
                      statement={v.statement}
                      badge={rank >= 0 ? rank + 1 : null}
                      testid={`value-${v.id}`}
                    />
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Working Genius rank */}
        {step === 3 && (
          <Card className="border border-slate-200" data-testid="step-genius">
            <CardHeader>
              <CardTitle>
                <StepHeader
                  idx={3}
                  label="Rank your Working Geniuses"
                  sub="Patrick Lencioni's 6 Working Geniuses. Put the ones that energise you at the top, and the ones that drain you at the bottom."
                />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500 mb-1">
                Top 2 = your <span className="font-semibold text-emerald-700">Geniuses</span> · Middle 2 = your <span className="font-semibold">Competencies</span> · Bottom 2 = your <span className="font-semibold text-rose-700">Frustrations</span>
              </p>
              <p className="text-xs text-slate-500 mb-3" data-testid="genius-drag-hint">
                <span className="font-semibold text-slate-700">Drag rows up or down</span> to reorder — or use the arrow buttons.
              </p>
              <ul className="space-y-2">
                {answers.working_genius_ranking.map((id, idx) => {
                  const area = content.working_genius_areas.find((a) => a.id === id);
                  if (!area) return null;
                  const zone =
                    idx < 2 ? "Genius (top)" : idx < 4 ? "Competency" : "Frustration (bottom)";
                  const zoneColor =
                    idx < 2 ? "text-emerald-700 bg-emerald-50 border-emerald-200" :
                    idx < 4 ? "text-slate-600 bg-slate-50 border-slate-200" :
                    "text-rose-700 bg-rose-50 border-rose-200";
                  const isDragging = dragIdx === idx;
                  const isDragOver = dragOverIdx === idx && dragIdx !== idx;
                  return (
                    <li
                      key={id}
                      draggable
                      onDragStart={(e) => {
                        setDragIdx(idx);
                        e.dataTransfer.effectAllowed = "move";
                        try { e.dataTransfer.setData("text/plain", String(idx)); } catch (_) {}
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        if (dragOverIdx !== idx) setDragOverIdx(idx);
                      }}
                      onDragLeave={() => {
                        if (dragOverIdx === idx) setDragOverIdx(null);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const from = dragIdx ?? Number(e.dataTransfer.getData("text/plain"));
                        if (!Number.isNaN(from)) reorderGenius(from, idx);
                        setDragIdx(null);
                        setDragOverIdx(null);
                      }}
                      onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                      className={`flex items-center gap-3 border rounded-lg p-3 cursor-grab active:cursor-grabbing select-none transition ${zoneColor.split(" ").slice(-1)[0]} ${isDragging ? "opacity-50 ring-2 ring-brand-gold" : ""} ${isDragOver ? "ring-2 ring-brand-navy/60 -translate-y-0.5" : ""}`}
                      data-testid={`genius-row-${id}`}
                    >
                      <div className="flex flex-col items-center gap-1" aria-hidden="true">
                        <GripVertical className="w-4 h-4 text-slate-400" />
                        <div className="flex flex-col">
                          <button type="button" onClick={() => moveGenius(idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`genius-up-${id}`} aria-label="Move up">
                            <GripVertical className="w-3.5 h-3.5 rotate-180" />
                          </button>
                          <button type="button" onClick={() => moveGenius(idx, +1)} disabled={idx === 5} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`genius-down-${id}`} aria-label="Move down">
                            <GripVertical className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-heading font-bold text-slate-900">{area.name}</span>
                          <span className="text-xs font-bold text-slate-400">{area.letter}</span>
                          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${zoneColor}`}>{zone}</span>
                        </div>
                        <p className="text-sm text-slate-600">{area.statement}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Results */}
        {step === 4 && existing && (
          <ResultsView
            profile={existing}
            content={content}
            onRetake={() => { setExisting(null); setStep(0); }}
            onShareToggle={toggleShare}
            onGenerateNarrative={() => generateNarrative(false)}
            genLoading={genNarrative}
            onDownloadPdf={downloadPdf}
          />
        )}

        {/* Footer nav */}
        {step < 4 && (
          <div className="flex items-center justify-between mt-6">
            <Button variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="strengths-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            {step < 3 ? (
              <Button onClick={() => setStep((s) => s + 1)} disabled={!canAdvance} className="btn-primary" data-testid="strengths-next-btn">
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={save} disabled={!canAdvance || saving} className="btn-primary" data-testid="strengths-save-btn">
                {saving ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</>) : (<>Save my profile <ArrowRight className="w-4 h-4 ml-1" /></>)}
              </Button>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="text-center mt-6">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-500" data-testid="strengths-done-btn">
              Back to dashboard
            </Button>
          </div>
        )}
      </div>
    </PageLayout>
  );
};


const ResultsView = ({ profile, content, onRetake, onShareToggle, onGenerateNarrative, genLoading, onDownloadPdf }) => {
  const themes = profile.top_themes_hydrated || [];
  const values = profile.top_values_hydrated || [];
  const split = profile.working_genius_split || { geniuses: [], competencies: [], frustrations: [] };
  const ws = profile.working_style || {};

  const wsLabel = (qid, val) => {
    const q = content.working_style_questions.find((x) => x.id === qid);
    if (!q) return val;
    const opt = q.options.find((o) => o.value === val);
    return opt?.label || val;
  };

  return (
    <div className="space-y-6" data-testid="strengths-results">
      {/* AI narrative */}
      <Card className="border border-[#FFD507]/40 bg-[#FFD507]/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-slate-900 flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-[#1E3A8A]" /> Your Strengths Story
          </CardTitle>
        </CardHeader>
        <CardContent>
          {profile.ai_narrative ? (
            <p className="text-slate-800 leading-relaxed" data-testid="strengths-narrative">{profile.ai_narrative}</p>
          ) : (
            <p className="text-slate-500 text-sm">{genLoading ? "Generating your personalised summary…" : "No summary yet."}</p>
          )}
          <div className="mt-3">
            <Button size="sm" variant="outline" onClick={onGenerateNarrative} disabled={genLoading} data-testid="regenerate-narrative-btn">
              {genLoading ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Working…</>) : (<><RefreshCcw className="w-3.5 h-3.5 mr-1" /> Regenerate</>)}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Top Themes & Values */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="border border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-slate-900"><Brain className="w-5 h-5 text-[#1E3A8A]" /> Top Themes</CardTitle>
          </CardHeader>
          <CardContent>
            {themes.length === 0 ? <p className="text-sm text-slate-500">None picked yet.</p> : (
              <ol className="space-y-2">
                {themes.map((t, i) => (
                  <li key={t.id} className="flex gap-3" data-testid={`result-theme-${t.id}`}>
                    <span className="w-7 h-7 rounded-full bg-[#FFD507] text-[#0F1B3D] text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                    <div>
                      <p className="font-semibold text-slate-900">{t.name}</p>
                      <p className="text-xs text-slate-600">{t.statement}</p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </CardContent>
        </Card>

        <Card className="border border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-slate-900"><Heart className="w-5 h-5 text-rose-500" /> Top Values</CardTitle>
          </CardHeader>
          <CardContent>
            {values.length === 0 ? <p className="text-sm text-slate-500">None picked yet.</p> : (
              <ol className="space-y-2">
                {values.map((v, i) => (
                  <li key={v.id} className="flex gap-3" data-testid={`result-value-${v.id}`}>
                    <span className="w-7 h-7 rounded-full bg-rose-100 text-rose-700 text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                    <div>
                      <p className="font-semibold text-slate-900">{v.name}</p>
                      <p className="text-xs text-slate-600">{v.statement}</p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Working Genius split */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-slate-900"><Zap className="w-5 h-5 text-[#FFD507]" /> Working Genius</CardTitle>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-3 gap-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="genius-section">
            <p className="text-xs uppercase tracking-wider text-emerald-700 font-bold mb-2">Your Geniuses (energise you)</p>
            {split.geniuses.length === 0 ? <p className="text-xs text-slate-500">Not ranked.</p> : split.geniuses.map((g) => (
              <div key={g.id} className="mb-2">
                <p className="font-semibold text-emerald-900">{g.letter} · {g.name}</p>
                <p className="text-xs text-emerald-800/80">{g.statement}</p>
              </div>
            ))}
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <p className="text-xs uppercase tracking-wider text-slate-600 font-bold mb-2">Competencies</p>
            {split.competencies.length === 0 ? <p className="text-xs text-slate-500">—</p> : split.competencies.map((g) => (
              <div key={g.id} className="mb-2">
                <p className="font-semibold text-slate-900">{g.letter} · {g.name}</p>
                <p className="text-xs text-slate-600">{g.statement}</p>
              </div>
            ))}
          </div>
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="frustrations-section">
            <p className="text-xs uppercase tracking-wider text-rose-700 font-bold mb-2">Frustrations (drain you)</p>
            {split.frustrations.length === 0 ? <p className="text-xs text-slate-500">Not ranked.</p> : split.frustrations.map((g) => (
              <div key={g.id} className="mb-2">
                <p className="font-semibold text-rose-900">{g.letter} · {g.name}</p>
                <p className="text-xs text-rose-800/80">{g.statement}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Working Style */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-slate-900"><Star className="w-5 h-5 text-[#1E3A8A]" /> Working Style</CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(ws).length === 0 ? <p className="text-sm text-slate-500">No working style answers yet.</p> : (
            <dl className="grid sm:grid-cols-2 gap-3 text-sm">
              {content.working_style_questions.map((q) => (
                <div key={q.id} className="bg-slate-50 rounded-lg p-3">
                  <dt className="text-xs text-slate-500">{q.label}</dt>
                  <dd className="font-semibold text-slate-900 mt-0.5">{wsLabel(q.id, ws[q.id]) || "—"}</dd>
                </div>
              ))}
            </dl>
          )}
        </CardContent>
      </Card>

      {/* Sharing */}
      <Card className="border border-slate-200" data-testid="strengths-sharing-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-slate-900"><Share2 className="w-5 h-5 text-[#1E3A8A]" /> Sharing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
            <input
              type="checkbox"
              checked={!!profile.share_talent_db}
              onChange={(e) => onShareToggle("share_talent_db", e.target.checked)}
              className="mt-1"
              data-testid="share-talent-toggle"
            />
            <div>
              <p className="font-semibold text-slate-900 flex items-center gap-2">
                {profile.share_talent_db ? <Eye className="w-4 h-4 text-emerald-600" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
                Share on my Talent DB profile
              </p>
              <p className="text-xs text-slate-500 mt-0.5">Vetted employers can see your top themes, values, and working geniuses. Skills and contact remain private until you choose to share.</p>
            </div>
          </label>
          <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
            <input
              type="checkbox"
              checked={!!profile.share_instructor}
              onChange={(e) => onShareToggle("share_instructor", e.target.checked)}
              className="mt-1"
              data-testid="share-instructor-toggle"
            />
            <div>
              <p className="font-semibold text-slate-900 flex items-center gap-2">
                {profile.share_instructor ? <Eye className="w-4 h-4 text-emerald-600" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
                Share with my instructor(s)
              </p>
              <p className="text-xs text-slate-500 mt-0.5">Help your instructor tailor the experience to how you learn best. Visible on the cohort roster only.</p>
            </div>
          </label>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between pt-2">
        <Button variant="outline" onClick={onRetake} data-testid="strengths-retake-btn">
          <RefreshCcw className="w-4 h-4 mr-1" /> Retake assessment
        </Button>
        <div className="flex items-center gap-3">
          <Button onClick={onDownloadPdf} className="btn-primary" data-testid="strengths-download-pdf-btn">
            <Download className="w-4 h-4 mr-1" /> Download as PDF
          </Button>
          <div className="hidden sm:flex items-center gap-2 text-emerald-700 text-sm">
            <CheckCircle2 className="w-4 h-4" /> Saved
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrengthsProfile;
